import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';

const RISK_BADGE_STYLES = {
  HIGH: 'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const RISK_THRESHOLDS = {
  HIGH: 70,
  MEDIUM: 40,
};

function RiskBadge({ status }) {
  const level = String(status || '').toUpperCase();

  return (
    <span
      className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border ${
        RISK_BADGE_STYLES[level] || 'bg-gray-100 text-gray-700 border-gray-200'
      }`}
    >
      {level}
    </span>
  );
}

function MetricCard({ title, value, colorClass = 'text-sap-text' }) {
  return (
    <div className="bg-white rounded-xl border p-5">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className={`text-4xl font-bold mt-3 ${colorClass}`}>{value}</div>
    </div>
  );
}

export default function VendorRiskDetail() {
  const { vendorName } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useApp();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get(`/vendors/risk/vendor/${encodeURIComponent(vendorName)}`);
      setData(result?.data || result);
    } catch (err) {
      setData(null);
      setError(err.message || 'Failed to load vendor risk');
    } finally {
      setLoading(false);
    }
  }, [vendorName]);

  useEffect(() => {
    dispatch({
      type: 'SET_BREADCRUMB',
      payload: ['Home', 'Vendor Management', 'Vendor risk', decodeURIComponent(vendorName || 'Vendor')],
    });
  }, [dispatch, vendorName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="py-16 text-center text-sm text-sap-textLight">Loading vendor risk…</div>;
  }

  if (error || !data) {
    return (
      <div className="py-16 text-center space-y-3">
        <p className="text-sm text-red-600">{error || 'Vendor not found'}</p>
        <button
          type="button"
          onClick={() => navigate('/vendor-management/risk')}
          className="text-xs text-sap-blue hover:underline"
        >
          ← Back to risk list
        </button>
      </div>
    );
  }

  const vendorScore = Number(data.vendor_rating || 0);
  const riskScore = Math.max(0, 100 - vendorScore);
  const deliveryScore = Number(data.delivery_score || 0);
  const qualityScore = Number(data.quality_score || 0);
  const procurementScore = Number(data.procurement_score || 0);
  const financialScore = Number(data.financial_score || 0);
  const vendorCategory = data.vendor_category || '-';
  const riskLevel = data.risk_level || '-';

  const riskColor =
    riskScore >= RISK_THRESHOLDS.HIGH
      ? 'text-red-600'
      : riskScore >= RISK_THRESHOLDS.MEDIUM
      ? 'text-orange-500'
      : 'text-green-600';

  const riskBarColor =
    riskScore >= RISK_THRESHOLDS.HIGH
      ? 'bg-red-500'
      : riskScore >= RISK_THRESHOLDS.MEDIUM
      ? 'bg-orange-500'
      : 'bg-green-500';

  return (
    <div className="space-y-4 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-sap-text">{data.vendor_name}</h1>
          <div className="flex flex-wrap gap-4 mt-2 text-xs text-sap-textLight">
            <span>
              <strong className="text-sap-text">Risk score:</strong> {riskScore.toFixed(2)}
            </span>
            <span>
              <strong className="text-sap-text">Vendor score:</strong> {vendorScore.toFixed(2)}
            </span>
            <span className="flex items-center gap-2">
              <strong className="text-sap-text">Status:</strong>
              <RiskBadge status={riskLevel} />
            </span>
            <span>
              <strong className="text-sap-text">Category:</strong> {vendorCategory}
            </span>
            <span>
              <strong className="text-sap-text">Rating:</strong> {data.vendor_rating ?? '—'}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/vendor-management/risk')}
          className="px-3 py-1.5 text-xs border rounded-lg bg-white hover:bg-sap-bg"
        >
          Back to risk list
        </button>
      </div>

      <div className="space-y-4">
        {/* Risk Score Card */}
        <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-6">
          <h3 className="text-sm font-semibold mb-4">Overall Risk Score</h3>
          <div className={`text-6xl font-light ${riskColor}`}>{riskScore.toFixed(2)}</div>
          <div className="mt-6 h-3 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-3 rounded-full ${riskBarColor}`} style={{ width: `${riskScore}%` }} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-sm font-semibold">Risk Level</h3>
            <div className="mt-4">
              <RiskBadge status={riskLevel} />
            </div>
          </div>

          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-sm font-semibold">Vendor Category</h3>
            <div className="text-lg font-bold text-blue-600 mt-4">{vendorCategory}</div>
          </div>

          <MetricCard
            title="Procurement Score"
            value={procurementScore.toFixed(2)}
            colorClass="text-orange-500"
          />
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard title="Delivery Score" value={deliveryScore.toFixed(2)} colorClass="text-red-500" />
          <MetricCard title="Quality Score" value={qualityScore.toFixed(2)} colorClass="text-green-600" />
          <MetricCard title="Financial Score" value={financialScore.toFixed(2)} colorClass="text-blue-600" />
        </div>
      </div>

      {/* Vendor Rating */}
      <div className="bg-white rounded-xl border p-5">
  <h3 className="text-sm font-semibold">Vendor Rating</h3>

  <div className="text-4xl font-bold text-purple-600 mt-3">
    {Number(data.vendor_rating || 0).toFixed(2)}
  </div>

  <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
    <div
      className="h-2 rounded-full bg-purple-500"
      style={{
        width: `${Math.min(Number(data.vendor_rating || 0), 100)}%`,
      }}
    />
  </div>
</div>
    </div>
  );
}
